import { useState, useRef, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { searchApi, sessionApi, datasetApi } from '../api/search'
import { PanelLeft, PanelLeftClose, Trash2, LogIn, UserPlus, RefreshCcw, Brain, MessageSquare, Sparkles, FileText } from 'lucide-react'
import QueryBuilder from '../components/QueryBuilder'
import FilterPanel from '../components/FilterPanel'
import { useSearchFilters } from '../contexts/SearchFiltersContext'
import { useAuth } from '../contexts/AuthContext'

interface SearchSidebarProps {
  sidebarVisible: boolean
  setSidebarVisible: (visible: boolean) => void
}

const TRIGGER_WIDTH = 200
const SIDEBAR_WIDTH = 576
const HIDE_DELAY = 2000

export default function SearchSidebar({ sidebarVisible, setSidebarVisible }: SearchSidebarProps) {
  const [query, setQuery] = useState('')
  const [maxResults, setMaxResults] = useState(100)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [searchId, setSearchId] = useState<string | null>(null)
  const [datasetName, setDatasetName] = useState('')
  const [sessionName, setSessionName] = useState('')
  const sidebarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastInTriggerZoneRef = useRef(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, user } = useAuth()
  const {
    activeFilters,
    setActiveFilters,
    filteredPmids,
    setFilteredPmids,
    setDatasetAnalysis,
    setDatasetTopicNames,
    setCurrentAnalysis,
    setCurrentTopicNames,
    currentAnalysis,
    currentTopicNames,
    loadedDatasetId,
    setLoadedDatasetId,
  } = useSearchFilters()

  // Sync with URL search_id
  useEffect(() => {
    const urlSearchId = searchParams.get('search_id')
    if (urlSearchId) {
      setSearchId(urlSearchId)
    }
  }, [searchParams])

  // Restore state
  useEffect(() => {
    const saved = localStorage.getItem('pubmed_search_state')
    if (saved) {
      try {
        const state = JSON.parse(saved)
        if (state.sessionId) setSessionId(state.sessionId)
        if (state.searchId) setSearchId(state.searchId)
        if (state.query) setQuery(state.query)
        if (state.maxResults) setMaxResults(state.maxResults)
      } catch (e) {
        console.error('Failed to restore session state:', e)
      }
    }
  }, [])

  useEffect(() => {
    const state = {
      sessionId,
      searchId,
      query,
      maxResults,
    }
    localStorage.setItem('pubmed_search_state', JSON.stringify(state))
  }, [sessionId, searchId, query, maxResults])

  const clearHideTimer = () => {
    if (sidebarTimeoutRef.current) {
      clearTimeout(sidebarTimeoutRef.current)
      sidebarTimeoutRef.current = null
    }
  }

  const scheduleHide = () => {
    clearHideTimer()
    sidebarTimeoutRef.current = setTimeout(() => {
      setSidebarVisible(false)
    }, HIDE_DELAY)
  }

  // Track mouse position globally
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const headerHeight = 60
      // When sidebar is hidden: trigger on left 200px edge
      // When sidebar is shown: trigger only when mouse is outside the sidebar panel
      const inTriggerZone = sidebarVisible
        ? e.clientX < SIDEBAR_WIDTH && e.clientY > headerHeight
        : e.clientX < TRIGGER_WIDTH && e.clientY > headerHeight
      
      if (inTriggerZone) {
        clearHideTimer()
        if (!sidebarVisible) {
          setSidebarVisible(true)
        }
      } else if (lastInTriggerZoneRef.current && !inTriggerZone) {
        scheduleHide()
      }
      
      lastInTriggerZoneRef.current = inTriggerZone
    }
    
    const handleMouseLeave = () => {
      scheduleHide()
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseleave', handleMouseLeave)
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseleave', handleMouseLeave)
      clearHideTimer()
    }
  }, [sidebarVisible, setSidebarVisible])

  const createSession = useMutation({
    mutationFn: sessionApi.createSession,
    onSuccess: (data) => {
      setSessionId(data.session_id)
      setSessionName('')
      // Clear query and search state for new session
      setQuery('')
      setSearchId(null)
      setFilteredPmids(new Set())
      setActiveFilters([])
      setDatasetName('')
      localStorage.removeItem('pubmed_search_state')
      queryClient.invalidateQueries({ queryKey: ['my-sessions'] })
    },
  })

  const createSearch = useMutation({
    mutationFn: searchApi.createSearch,
    onSuccess: (data) => {
      setSearchId(data.search_id)
      navigate(`/?search_id=${data.search_id}`)
    },
  })

  const { data: searchResult } = useQuery({
    queryKey: ['search', searchId],
    queryFn: () => searchApi.getSearch(searchId!),
    enabled: !!searchId,
    refetchInterval: (query) => {
      if (query?.state?.data?.search?.status === 'running') return 2000
      return false
    },
  })

  const { data: mySessions } = useQuery({
    queryKey: ['my-sessions'],
    queryFn: () => sessionApi.listMySessions(),
    enabled: isAuthenticated,
  })

  const { data: myDatasets } = useQuery({
    queryKey: ['my-datasets', sessionId],
    queryFn: () => datasetApi.listDatasets(sessionId || undefined),
    enabled: isAuthenticated && !!sessionId,
  })

  const [selectedDatasetIds, setSelectedDatasetIds] = useState<Set<string>>(new Set())
  const [mergeName, setMergeName] = useState('')

  const saveDataset = useMutation({
    mutationFn: async (data: any) => {
      console.log('DEBUG saveDataset mutationFn called with:', data)
      try {
        const result = await datasetApi.createDataset(data)
        console.log('DEBUG saveDataset success:', result)
        return result
      } catch (error) {
        console.error('DEBUG saveDataset error:', error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-datasets'] })
      setDatasetName('')
      alert('Dataset saved successfully!')
    },
    onError: (error: any) => {
      console.error('Save dataset error:', error)
      alert('Failed to save dataset: ' + (error?.response?.data?.detail || error.message || 'Unknown error'))
    }
  })

  const updateDataset = useMutation({
    mutationFn: ({ datasetId, data }: { datasetId: string; data: any }) =>
      datasetApi.updateDataset(datasetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-datasets'] })
      setDatasetName('')
      alert('Dataset updated successfully!')
    },
    onError: (error: any) => {
      console.error('Update dataset error:', error)
      alert('Failed to update dataset: ' + (error?.response?.data?.detail || error.message || 'Unknown error'))
    }
  })

  const loadDataset = useMutation({
    mutationFn: (datasetId: string) => datasetApi.getDataset(datasetId),
    onSuccess: (data) => {
      // Clear any previous filters/state first
      setActiveFilters(data.dataset.filter_state || [])
      const pmids: string[] = data.pmids
        || (data.articles ? data.articles.map((a: any) => a.pmid) : [])
      if (pmids.length > 0) {
        setFilteredPmids(new Set(pmids))
      } else {
        setFilteredPmids(new Set())
      }
      setLoadedDatasetId(data.dataset.dataset_id)
      setDatasetName(data.dataset.name)
      // Restore analysis data if present
      if (data.dataset.analysis_results) {
        setDatasetAnalysis(data.dataset.analysis_results)
        setCurrentAnalysis(data.dataset.analysis_results)
      } else {
        setDatasetAnalysis(null)
        setCurrentAnalysis(null)
      }
      if (data.dataset.topic_names) {
        setDatasetTopicNames(data.dataset.topic_names)
        setCurrentTopicNames(data.dataset.topic_names)
      } else {
        setDatasetTopicNames({})
        setCurrentTopicNames({})
      }
    // Always navigate to the search page when loading a dataset
      if (data.dataset.search_id) {
        setSearchId(data.dataset.search_id)
        navigate(`/?search_id=${data.dataset.search_id}`)
      }
    },
  })

  const deleteSession = useMutation({
    mutationFn: sessionApi.deleteSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-sessions'] })
      if (sessionId && !mySessions?.find(s => s.session_id === sessionId)) {
        setSessionId(null)
      }
    },
  })

  const deleteDataset = useMutation({
    mutationFn: datasetApi.deleteDataset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-datasets'] })
    },
    onError: (error: any) => {
      alert('Failed to delete dataset: ' + (error?.response?.data?.detail || error.message || 'Unknown error'))
    }
  })

  const mergeDatasets = useMutation({
    mutationFn: (data: { name: string; dataset_ids: string[]; session_id?: string | null }) =>
      datasetApi.mergeDatasets(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-datasets'] })
      setSelectedDatasetIds(new Set())
      setMergeName('')
      alert('Datasets merged successfully!')
    },
    onError: (error: any) => {
      alert('Failed to merge datasets: ' + (error?.response?.data?.detail || error.message || 'Unknown error'))
    },
  })

  const replaceDatasetArticles = useMutation({
    mutationFn: (vars: { datasetId: string; search_id: string; pmids?: string[] | null }) =>
      datasetApi.replaceDatasetArticles(vars.datasetId, { search_id: vars.search_id, pmids: vars.pmids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-datasets'] })
      alert('Dataset updated with current table!')
    },
    onError: (error: any) => {
      alert('Failed to update dataset: ' + (error?.response?.data?.detail || error.message || 'Unknown error'))
    },
  })

  const loadSession = useMutation({
    mutationFn: (id: string) => sessionApi.getSession(id),
    onSuccess: (data) => {
      setSessionId(data.session_id)
      if (data.search_id) {
        setSearchId(data.search_id)
        navigate(`/?search_id=${data.search_id}`)
      }
    },
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return
    // Clear filters and dataset state for new search
    setFilteredPmids(new Set())
    setActiveFilters([])
    setDatasetAnalysis(null)
    setDatasetTopicNames({})
    setCurrentAnalysis(null)
    setCurrentTopicNames({})
    setLoadedDatasetId(null)
    createSearch.mutate({
      query: query.trim(),
      max_results: maxResults,
    })
  }

  const handleQueryBuilt = (builtQuery: string) => {
    setQuery(builtQuery)
  }

  const handleSaveDataset = () => {
    console.log('handleSaveDataset called', { datasetName, searchId, sessionId })
    if (!datasetName.trim() || !searchId) {
      console.log('Early return: missing datasetName or searchId')
      return
    }
    
    const data = {
      name: datasetName.trim(),
      search_id: searchId,
      session_id: sessionId || undefined,
      pmids: filteredPmids.size > 0 ? Array.from(filteredPmids) : undefined,
      filter_state: activeFilters.length > 0 ? { filters: activeFilters } : undefined,
    }
    console.log('Saving dataset with data:', data)
    
    // Check if a dataset with the same name exists for this search
    const existingDataset = myDatasets?.find(
      (d: any) => d.name === datasetName.trim() && d.search_id === searchId
    )
    
    if (existingDataset) {
      // Update existing dataset
      console.log('Updating existing dataset:', existingDataset.dataset_id)
      updateDataset.mutate({ datasetId: existingDataset.dataset_id, data })
    } else {
      // Create new dataset
      console.log('Creating new dataset')
      saveDataset.mutate(data)
    }
  }

  const handleFiltersChange = (filters: any[]) => {
    setActiveFilters(filters)
  }

  const handleFilteredPmids = (pmids: string[]) => {
    setFilteredPmids(new Set(pmids))
  }

  const searchPmids: string[] = (searchResult as any)?.pmids
    || (searchResult?.articles ? searchResult.articles.map((a: any) => a.pmid) : [])
  const hasResults = !!searchResult && searchPmids.length > 0

  return (
    <>
      {/* SIDEBAR */}
      <div
        className={`fixed left-0 top-[60px] bottom-0 bg-gray-50 border-r border-gray-200 z-50 transition-transform duration-300 ease-in-out ${
          sidebarVisible ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: SIDEBAR_WIDTH }}
      >
        <div className="h-full overflow-y-auto p-4 space-y-4" style={{ width: SIDEBAR_WIDTH }}>
          {/* Toggle button */}
          <div className="flex justify-end">
            <button
              onClick={() => {
                clearHideTimer()
                setSidebarVisible(false)
              }}
              className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors"
              title="Hide sidebar"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          </div>

          {/* Auth Section */}
          {!isAuthenticated ? (
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Welcome to Abstrata</h3>
              <p className="text-sm text-gray-600 mb-4">
                Please sign in or create an account to save sessions and datasets.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
                >
                  <LogIn size={16} />
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50 font-medium flex items-center justify-center gap-2"
                >
                  <UserPlus size={16} />
                  Create Account
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* User Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-800">
                  <strong>Welcome, {user?.name || user?.email}</strong>
                </div>
              </div>

              {/* Session Selector */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Session</h3>
                </div>
                
                {/* Session dropdown */}
                <div className="mb-3">
                  <select
                    value={sessionId || ''}
                    onChange={(e) => {
                      const selectedSessionId = e.target.value
                      if (selectedSessionId) {
                        loadSession.mutate(selectedSessionId)
                      } else {
                        setSessionId(null)
                        setQuery('')
                        setSearchId(null)
                        setFilteredPmids(new Set())
                        setActiveFilters([])
                        setDatasetName('')
                        localStorage.removeItem('pubmed_search_state')
                      }
                    }}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  >
                    <option value="">-- Select a session --</option>
                    {mySessions?.map((session: any) => (
                      <option key={session.session_id} value={session.session_id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Create new session */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    placeholder="New session name..."
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <button
                    onClick={() => {
                      if (!sessionName.trim()) return
                      createSession.mutate({
                        name: sessionName.trim(),
                        search_id: searchId || undefined,
                      })
                    }}
                    disabled={createSession.isPending || !sessionName.trim()}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    New
                  </button>
                </div>
                
                {sessionId && (
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-blue-600">
                      Active: {mySessions?.find((s: any) => s.session_id === sessionId)?.name}
                    </span>
                    <button
                      onClick={() => {
                        if (confirm('Delete this session?')) {
                          deleteSession.mutate(sessionId)
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 size={12} />
                      Delete
                    </button>
                  </div>
                )}
              </div>

              {/* Search Form */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Search PubMed</h3>

                <form onSubmit={handleSearch} className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Query</label>
                    <textarea
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="e.g., extracellular vesicles[tiab] AND 2024[dp]"
                      rows={3}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <QueryBuilder onQueryBuilt={handleQueryBuilt} />

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Max Results</label>
                      <input
                        type="number"
                        value={maxResults}
                        onChange={(e) => setMaxResults(Number(e.target.value))}
                        min={10}
                        max={200000}
                        step={10}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                      {maxResults > 50000 && (
                        <p className="text-[10px] text-amber-600 mt-1">
                          Large queries (&gt;50k) can take several minutes to download from PubMed.
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={createSearch.isPending || !query.trim()}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {createSearch.isPending ? 'Searching...' : 'Search'}
                  </button>

                </form>
              </div>

              {/* Filters */}
              {hasResults && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Filters</h3>
                  {(() => {
                    // Compute available topics from analysis data
                    const analysis = currentAnalysis
                    const topicNames = currentTopicNames
                    
                    let availableTopics: { id: number; name?: string; count?: number }[] = []
                    const topicMapping: Record<string, number> = {}
                    
                    // Build topic mapping from documents (always needed for filtering)
                    if (analysis?.documents) {
                      analysis.documents.forEach((d: any) => {
                        const topic = d.dominant_topic ?? d.topic
                        if (topic != null && d.pmid) {
                          topicMapping[d.pmid] = topic
                        }
                      })
                    }
                    
                    // Build available topics list
                    if (analysis?.topic_distribution) {
                      availableTopics = analysis.topic_distribution.map((t: any) => ({
                        id: t.topic,
                        name: topicNames[String(t.topic + 1)] || `Topic ${t.topic + 1}`,
                        count: t.n_docs,
                      }))
                    } else if (analysis?.documents) {
                      // Count docs per topic
                      const topicCounts: Record<number, number> = {}
                      analysis.documents.forEach((d: any) => {
                        const topic = d.dominant_topic ?? d.topic
                        if (topic != null) {
                          topicCounts[topic] = (topicCounts[topic] || 0) + 1
                        }
                      })
                      availableTopics = Object.entries(topicCounts).map(([topic, count]) => ({
                        id: Number(topic),
                        name: topicNames[String(Number(topic) + 1)] || `Topic ${Number(topic) + 1}`,
                        count,
                      })).sort((a, b) => a.id - b.id)
                    }
                    
                    return (
                      <FilterPanel
                        searchId={searchId!}
                        articles={searchPmids.map(p => ({ pmid: p }))}
                        onFiltersChange={handleFiltersChange}
                        onFilteredPmids={handleFilteredPmids}
                        availableTopics={availableTopics}
                        topicMapping={topicMapping}
                      />
                    )
                  })()}
                </div>
              )}

              {/* Dataset Save */}
              {hasResults && sessionId && (
                <div className="bg-white rounded-lg shadow-sm border p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {myDatasets?.find((d: any) => d.name === datasetName.trim() && d.search_id === searchId)
                      ? 'Update Dataset'
                      : 'Save Dataset'}
                  </h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={datasetName}
                      onChange={(e) => setDatasetName(e.target.value)}
                      placeholder="Dataset name..."
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <button
                      onClick={handleSaveDataset}
                      disabled={!datasetName.trim() || saveDataset.isPending || updateDataset.isPending}
                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {myDatasets?.find((d: any) => d.name === datasetName.trim() && d.search_id === searchId)
                        ? 'Update'
                        : 'Save'}
                    </button>
                  </div>
                </div>
              )}
              
              {hasResults && !sessionId && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-700">
                    Please select or create a session to save datasets.
                  </p>
                </div>
              )}

              {/* My Datasets */}
              <div className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Session Datasets</h3>
                </div>

                {selectedDatasetIds.size >= 2 && (
                  <div className="mb-2 p-2 border border-blue-200 bg-blue-50 rounded">
                    <div className="text-xs text-blue-800 mb-1.5">
                      Merge {selectedDatasetIds.size} datasets into a new one (deduplicated union of articles).
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={mergeName}
                        onChange={(e) => setMergeName(e.target.value)}
                        placeholder="New dataset name…"
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                      />
                      <button
                        onClick={() => {
                          if (!mergeName.trim()) return
                          mergeDatasets.mutate({
                            name: mergeName.trim(),
                            dataset_ids: Array.from(selectedDatasetIds),
                            session_id: sessionId || undefined,
                          })
                        }}
                        disabled={!mergeName.trim() || mergeDatasets.isPending}
                        className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Merge
                      </button>
                      <button
                        onClick={() => { setSelectedDatasetIds(new Set()); setMergeName('') }}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {!sessionId ? (
                  <p className="text-xs text-gray-400">Select a session to view its datasets</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {myDatasets?.map((dataset: any) => {
                      const selected = selectedDatasetIds.has(dataset.dataset_id)
                      const canUpdate = !!searchId && filteredPmids.size > 0
                      return (
                        <div
                          key={dataset.dataset_id}
                          className={`flex items-center gap-2 p-2 rounded text-sm hover:bg-gray-50 ${
                            loadedDatasetId === dataset.dataset_id ? 'bg-blue-50 border border-blue-200' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              setSelectedDatasetIds(prev => {
                                const next = new Set(prev)
                                if (e.target.checked) next.add(dataset.dataset_id)
                                else next.delete(dataset.dataset_id)
                                return next
                              })
                            }}
                            title="Select for merge"
                            className="cursor-pointer"
                          />
                          <span className="flex-1 truncate" title={dataset.name}>{dataset.name}</span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {/* Article count */}
                            <span className="flex items-center gap-0.5 text-[10px] text-gray-400" title={`${dataset.article_count || 0} articles`}>
                              <FileText size={12} />
                              {dataset.article_count || 0}
                            </span>
                            {/* Analysis indicators */}
                            {(dataset.analysis_results || dataset.topic_names) && (
                              <span title={dataset.annotation_analysis_name
                                ? `Annotated by ${dataset.annotation_analysis_name} (${dataset.analysis_type || 'topic'})`
                                : 'Topic analysis available'}>
                                <Brain size={14} className={dataset.annotation_analysis_id ? 'text-violet-600' : 'text-violet-300'} />
                              </span>
                            )}
                            {dataset.ai_insights && (
                              <span title="AI analysis conducted">
                                <Sparkles size={14} className="text-amber-500" />
                              </span>
                            )}
                            {dataset.has_rag && (
                              <span title="RAG index built">
                                <MessageSquare size={14} className="text-emerald-500" />
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => loadDataset.mutate(dataset.dataset_id)}
                              disabled={loadDataset.isPending}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                              title="Load dataset"
                            >
                              Load
                            </button>
                            <button
                              onClick={() => {
                                if (!canUpdate || !searchId) return
                                if (window.confirm(
                                  `Replace the articles of "${dataset.name}" with the ${filteredPmids.size} currently displayed articles? The name and any analysis will be kept.`
                                )) {
                                  replaceDatasetArticles.mutate({
                                    datasetId: dataset.dataset_id,
                                    search_id: searchId,
                                    pmids: Array.from(filteredPmids),
                                  })
                                }
                              }}
                              disabled={!canUpdate || replaceDatasetArticles.isPending}
                              className="p-1 text-gray-500 hover:text-blue-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={canUpdate ? 'Update with current table' : 'Run a search first to enable update'}
                            >
                              <RefreshCcw size={14} />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Delete dataset "${dataset.name}"?`)) {
                                  deleteDataset.mutate(dataset.dataset_id)
                                }
                              }}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Delete dataset"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                    {(!myDatasets || myDatasets.length === 0) && (
                      <p className="text-xs text-gray-400">No datasets in this session</p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* TAB: visible when sidebar is hidden */}
      {!sidebarVisible && (
        <div 
          className="fixed left-0 top-1/2 -translate-y-1/2 z-50 bg-white border border-l-0 border-gray-200 rounded-r-lg shadow-md p-2 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setSidebarVisible(true)}
          title="Show search panel"
        >
          <PanelLeft className="w-5 h-5 text-gray-500" />
        </div>
      )}
    </>
  )
}
